import { createContext, useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grow,
} from "@mui/material";

import { AccessTimeRounded, LogoutRounded, ArrowForwardRounded } from "@mui/icons-material";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [helpPromptExpiresAt, setHelpPromptExpiresAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutos
  const SESSION_MAX_DURATION = 3 * 60 * 60 * 1000; // 3 horas

  const inactivityTimer = useRef(null);
  const sessionTimer = useRef(null);
  const warningTimer = useRef(null);
  const sessionExpiresAt = useRef(null);

  // ---------------------- FUNCIONES DE LOGOUT AUTOMÁTICO ----------------------

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer.current);
    clearTimeout(warningTimer.current);
    setShowModal(false);

    // Mostrar modal 1 minuto antes de cerrar por inactividad
    warningTimer.current = setTimeout(() => {
      setShowModal(true);
    }, INACTIVITY_LIMIT - 1 * 60 * 1000);

    inactivityTimer.current = setTimeout(() => {
      logout();
    }, INACTIVITY_LIMIT);
  };

  const startSessionTimer = () => {
    clearTimeout(sessionTimer.current);
    const remaining = sessionExpiresAt.current - Date.now();
    if (remaining <= 0) {
      logout();
      return;
    }
    sessionTimer.current = setTimeout(logout, remaining);
  };

  const clearTimers = () => {
    clearTimeout(inactivityTimer.current);
    clearTimeout(sessionTimer.current);
    clearTimeout(warningTimer.current);
  };

  const continueSession = () => {
    setShowModal(false);
    resetInactivityTimer(); // reinicia timers
  };

  useEffect(() => {
    if (!token) return;

    const events = ["click", "scroll", "keydown"];
    events.forEach((event) => window.addEventListener(event, resetInactivityTimer));

    resetInactivityTimer();
    startSessionTimer();

    return () => {
      clearTimers();
      events.forEach((event) => window.removeEventListener(event, resetInactivityTimer));
    };
  }, [token]);

  // ---------------------- CARGAR SESIÓN AL INICIAR ----------------------

  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = localStorage.getItem("user");
      const storedToken = localStorage.getItem("token");

      if (storedUser && storedToken) {
        try {
          const restoredUser = JSON.parse(storedUser);
          if (!restoredUser || typeof restoredUser !== "object") {
            throw new Error("Sesión almacenada inválida");
          }
          const storedExpiry = Number(localStorage.getItem("sessionExpiresAt"));
          // Las sesiones previas se migran una vez sin interrumpir el acceso.
          const expiry = storedExpiry > 0 ? storedExpiry : Date.now() + SESSION_MAX_DURATION;
          if (expiry <= Date.now()) {
            logout();
          } else {
            sessionExpiresAt.current = expiry;
            localStorage.setItem("sessionExpiresAt", String(expiry));
            const savedHelpExpiry = Number(localStorage.getItem("helpPromptExpiresAt"));
            setHelpPromptExpiresAt(
              savedHelpExpiry > Date.now() && savedHelpExpiry <= expiry ? savedHelpExpiry : null
            );
            setUser(restoredUser);
            setToken(storedToken);
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          logout();
        }
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // ---------------------- FUNCIONES DE AUTENTICACIÓN ----------------------

  const login = (userData) => {
    userData = typeof userData === "string" ? JSON.parse(userData) : userData;
    if (!userData || typeof userData.token !== "string" || !userData.token) {
      throw new Error("Respuesta de autenticación inválida");
    }
    const userToStore = {
      ...userData,
      role: userData.role || userData.user?.role,
    };

    sessionExpiresAt.current = Date.now() + SESSION_MAX_DURATION;
    localStorage.setItem("sessionExpiresAt", String(sessionExpiresAt.current));
    const helpExpiry = Date.now() + 3 * 60 * 1000;
    setHelpPromptExpiresAt(helpExpiry);
    localStorage.setItem("helpPromptExpiresAt", String(helpExpiry));
    setUser(userToStore);
    setToken(userData.token);
    localStorage.setItem("user", JSON.stringify(userToStore) || "{}");
    localStorage.setItem("token", userData.token || "");
    navigate("/dashboard");
  };

  const logout = () => {
    clearTimers();
    setShowModal(false);
    setHelpPromptExpiresAt(null);
    localStorage.removeItem("helpPromptExpiresAt");
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("sessionExpiresAt");
    sessionExpiresAt.current = null;
    navigate("/authentication/sign-in");
  };

  const hasRole = (requiredRoles = []) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!user) return false;

    const userRole = user?.role?.name || user?.role;
    return requiredRoles.some(
      (role) => String(role).toLowerCase() === String(userRole).toLowerCase()
    );
  };

  const hasPermission = (requiredPermission) => {
    if (!user || !user.role) return false;
    if (user.role.name === "superadmin") return true;

    return user.role.more?.[requiredPermission] === true;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        helpPromptExpiresAt,
        isLoading,
        login,
        logout,
        hasRole,
        hasPermission,
      }}
    >
      {children}

      <Dialog
        open={showModal}
        TransitionComponent={Grow}
        keepMounted
        onClose={() => {}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            m: 2,
            width: "calc(100% - 32px)",
            maxWidth: 460,
            overflow: "hidden",
            bgcolor: "#ffffff",
            boxShadow: "0 24px 64px rgba(15,23,42,0.24)",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            py: 2.5,
            background: "linear-gradient(110deg, #153448, #155e63)",
            color: "#ffffff",
            fontSize: 20,
            fontWeight: 700,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              padding: 10,
              borderRadius: 12,
              background: "rgba(255,255,255,0.12)",
            }}
          >
            <AccessTimeRounded sx={{ fontSize: 24, color: "#ffffff" }} aria-hidden="true" />
          </span>
          ¿Sigues ahí?
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: "24px !important", pb: 3 }}>
          <img
            src="https://cdn.dribbble.com/users/285475/screenshots/2083086/dribbble_1.gif"
            alt="Advertencia de sesión"
            style={{
              display: "block",
              width: "100%",
              maxWidth: 200,
              height: "auto",
              margin: "0 auto 16px",
              borderRadius: 12,
            }}
          />
          <DialogContentText
            id="alert-dialog-description"
            sx={{ fontSize: 15, lineHeight: 1.7, color: "#334155", m: 0 }}
          >
            Tu sesión está a punto de cerrarse por inactividad. Continúa para seguir trabajando en
            Goper.
          </DialogContentText>
          <p
            style={{
              margin: "18px 0 0",
              padding: "12px 14px",
              borderRadius: 8,
              background: "#f0f6f8",
              color: "#164858",
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            Por seguridad, cerramos las sesiones que permanecen inactivas.
          </p>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            gap: 1.25,
            borderTop: "1px solid #e2e8f0",
            bgcolor: "#f8fafc",
            flexDirection: { xs: "column-reverse", sm: "row" },
            "& > :not(style) ~ :not(style)": { ml: 0 },
            "& .MuiButton-root": {
              width: { xs: "100%", sm: "auto" },
              minHeight: 42,
              fontSize: 13,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2,
            },
          }}
        >
          <Button
            onClick={logout}
            variant="outlined"
            startIcon={<LogoutRounded />}
            sx={{
              color: "#b91c1c !important",
              borderColor: "#fecaca",
              bgcolor: "#ffffff",
              "&:hover": { borderColor: "#b91c1c", bgcolor: "#fff1f2" },
            }}
          >
            Cerrar sesión
          </Button>
          <Button
            onClick={continueSession}
            variant="contained"
            endIcon={<ArrowForwardRounded />}
            disableElevation
            sx={{
              bgcolor: "#155e63",
              color: "#ffffff !important",
              "&:hover": { bgcolor: "#104b50" },
            }}
          >
            Continuar sesión
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
